import { FuelGridContainer } from '@frete.com/fuel-react';
import actions from 'constants/reducers';
import { DriverTagEnum, TTruckerItem } from 'context/reducers/truckers.type';
import { useAmplitude } from 'hooks/useAmplitude';
import { useAppContext } from 'hooks/useAppContext';
import { useFilteredData } from 'hooks/useFilteredData';
import { useGlobals } from 'hooks/useGlobals';
import { useLoadTruckers } from 'hooks/useLoadTruckers';
import { usePayPerUseExtraTruckers } from 'hooks/usePayPerUseExtraTruckers';
import { useTracking } from 'hooks/useTracking';
import { useUnlockSuggestedTruckersEvents } from 'hooks/useUnlockSuggestedTruckersEvents';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bindSearch } from 'services/trucker';
import { isVisualized, markAsVisualized } from 'services/truckerVisualized/truckerVisualized';
import { getStateByName } from 'utils/getStatesAbbreviation';
import { useFlagr } from 'hooks/useFlagr';
import { useDrawerCheckout } from 'hooks/useDrawerCheckout';
import { CardAnimations } from './constants';
import DesktopWrapper from './DesktopWrapper';
import MobileWrapper from './MobileWrapper';
import * as Styled from './styled';
import { TruckerContainer } from './styled';
import { TruckerInformationProps, TTuckerListItemProps } from './types';

const TruckerListItem: FC<TTuckerListItemProps> = ({
  truckerItem,
  startAsVisualized = false,
  bindSearchTag = false,
  isLocked,
  showDivider,
}) => {
  const globals = useGlobals();
  const userId = globals?.user?.data?.id;

  const { person, vehicle, checkin, tag, lastLocation } = truckerItem;
  const { fetchTruckers } = useLoadTruckers('lazy');
  const selectedTimer = useRef(null);
  const statusTimer = useRef(null);
  const { sendAmplitude } = useAmplitude();
  const { sendTracking } = useTracking();
  const { freightId } = useParams();
  const { isPayPerUsePlan } = usePayPerUseExtraTruckers();
  const { sendUnlockSuggestedTruckersEvent } = useUnlockSuggestedTruckersEvents();

  const {
    state: {
      truckers: {
        radius,
        activeDriver,
        profileAutoLoad,
        meta: {
          freight: { code },
        },
      },
      searchTruckers: { dataSearch },
      truckerProfile: { selected, status },
      shareFreights: { truckers },
      deviceSize: { isDesktop },
    },
    dispatch,
  } = useAppContext();

  const navigate = useNavigate();
  const { filteredDataSuggested } = useFilteredData();

  const [cardVisualized, setCardVisualized] = useState(false);
  const [animation, setAnimation] = useState<CardAnimations>(CardAnimations.NONE);

  const { hasAccess } = useFlagr(Number(process.env.FLAGR_DRAWER_CHECKOUT_PLUS));
  const { viewDrawerCheckout } = useDrawerCheckout();

  const isLocationByGeolocation = !!lastLocation && lastLocation?.verified && !!person?.whereIs;
  const isLocationByCheckin = !!checkin?.createdAt && !!checkin?.originCity;
  const hasCheckin = isLocationByGeolocation || isLocationByCheckin;

  useEffect(() => {
    setCardVisualized(isVisualized(code, vehicle.id, userId, person?.id));
  }, [cardVisualized]);

  const getDriverTagEnumKeyByValue = (value: number) =>
    Object.keys(DriverTagEnum).find((key) => DriverTagEnum[key] === value);

  const getId = () => person?.id;

  const getActiveDriver = () => {
    if (bindSearchTag) return dataSearch;
    return filteredDataSuggested.filter((trucker: TTruckerItem) => getId() === trucker.person.id)?.[0];
  };

  const setActive = () => {
    const selectedDriver = getActiveDriver();
    if (selectedDriver) {
      dispatch({
        type: 'truckers_driver_active_set',
        payload: { activeDriver: { ...selectedDriver, id: getId() } },
      });
      navigate(`${getId()}`);
    }
  };

  const setVisualized = () => {
    markAsVisualized(code, vehicle.id, userId, person?.id);
    setCardVisualized(isVisualized(code, vehicle.id, userId, person?.id));
  };

  const setSearchTag = () => {
    bindSearch({ encodedFreightId: freightId, vehicleId: vehicle?.id, personUuid: person.uuid });
  };

  const markCard = () => {
    setActive();
    setVisualized();
  };

  const dispatchAmplitude = () => {
    sendAmplitude('trucker_listing_profile_visited', {
      source: 'MS_meusfretes',
      type: 'listagem-motorista',
      truckerId: getId(),
      tag: tag === 0 ? 'N/A' : getDriverTagEnumKeyByValue(tag),
      codeRegion: person.areaCodes,
      destiny: checkin?.destination ? checkin?.destination : 'N/A',
      freightCode: freightId,
      isVerified: person.verified,
    });
    sendTracking({
      url: window.location.href,
      referrer: document.referrer,
      event: {
        name: `trucker_listing_profile_visited`,
      },
      data: {
        source: 'aba-sugeridos',
        type: 'listagem-motorista',
        truckerId: `hashed|${getId()}`,
        tag: tag === 0 ? 'N/A' : getDriverTagEnumKeyByValue(tag),
        codeRegion: person.areaCodes,
        destiny: checkin?.destination ? checkin?.destination : 'N/A',
        freightId: `hashed|${freightId}`,
        isVerified: person.verified,
      },
    });
  };

  const handleOnclick = (e: React.MouseEvent) => {
    const { type } = e.target as HTMLInputElement;

    if (type === 'checkbox') return;

    if (isLocked) {
      if (isPayPerUsePlan) {
        sendUnlockSuggestedTruckersEvent('click-blocked-trucker');
        hasAccess ? viewDrawerCheckout() : dispatch({ type: actions.modals.setOpenPurchaseExtraSuggestedTruckers });
      }

      return;
    }

    if (activeDriver?.id !== getId() || (activeDriver?.id === getId() && profileAutoLoad) || !isDesktop) {
      dispatchAmplitude();
      markCard();
    }
    if (bindSearchTag) {
      setSearchTag();
    }
  };

  useEffect(() => {
    if (startAsVisualized && isDesktop) {
      markCard();
      setSearchTag();
    }
  }, [isDesktop]);

  useEffect(() => {
    if (selected?.id === getId()) {
      setAnimation(CardAnimations.RIGHT);
      selectedTimer.current = setTimeout(() => {
        if (selected.status === 'discarded') {
          fetchTruckers(radius);
        }
        dispatch({ type: 'trucker_profile_set', payload: null });
      }, 850);
    }
    return () => clearTimeout(selectedTimer.current);
  }, [selected]);

  useEffect(() => {
    if (status?.id === getId()) {
      setAnimation(CardAnimations.RIGHT);
      statusTimer.current = setTimeout(() => {
        dispatch({ type: 'tab_active_set', payload: status.status });
        dispatch({ type: 'trucker_profile_trading_status_set', payload: null });
      }, 850);
    }
    return () => clearTimeout(statusTimer.current);
  }, [status]);

  const getTruckerOrigin = () => {
    if (isLocationByGeolocation) return person.whereIs;
    return `${checkin?.originCity}, ${getStateByName(checkin?.originState)?.abbreviation ?? checkin?.originState}`;
  };

  const handleSelectTrucker = (checked: boolean) => {
    const value = { uuid: person.uuid, isShared: person.alreadyNotified };
    if (checked) {
      dispatch({ type: 'set_selected_trucker', payload: value });
    } else {
      dispatch({ type: 'remove_selected_trucker', payload: value });
    }
  };

  const getProps = (): TruckerInformationProps => ({
    ...truckerItem,
    hasCheckin,
    truckerOrigin: getTruckerOrigin(),
    isMobile: !isDesktop,
    onSelect: !isLocked ? handleSelectTrucker : null,
    selected: truckers.some((trucker) => trucker.uuid === person.uuid),
    isLocked,
  });

  return (
    <Styled.ListItem
      onClick={handleOnclick}
      data-testid="trucker-list-item"
      $visualized={cardVisualized}
      $active={person?.id === activeDriver?.id}
      $animation={animation}
      $disableClick={isLocked && !isPayPerUsePlan}
    >
      <TruckerContainer $showDivider={showDivider}>
        {!isDesktop ? (
          <Styled.GridContainerMobile gapRow={16}>
            <MobileWrapper {...getProps()} />
          </Styled.GridContainerMobile>
        ) : (
          <FuelGridContainer gapRow={0}>
            <DesktopWrapper {...getProps()} />
          </FuelGridContainer>
        )}
      </TruckerContainer>
    </Styled.ListItem>
  );
};

export default TruckerListItem;
